/**
 * EN — mirror of the `ru.ts` structure.
 *
 * Kept 1:1 with `ru.ts`: every key in RU has an English counterpart
 * here. Structure drift is caught by TypeScript via `RuMessages`.
 *
 * Full translation pass: EN copy is hand-written for FMCG / retail
 * marketing directors. Tone: confident, active voice, B2B.
 */

import type { RuMessages } from '@/i18n/locales/ru';

const en: RuMessages = {
  common: {
    brand: 'Bafsy',
    tagline: 'The platform behind standout promos',
  },
  nav: {
    home: 'Home',
    platform: 'Platform',
    solutions: 'Solutions',
    workflow: 'How we work',
    cases: 'Cases',
    pricing: 'Pricing',
    calculator: 'Calculator',
    brief: 'Brief',
    contact: 'Contact',
    privacy: 'Privacy policy',
    cta: 'Submit a request',
    menuOpen: 'Menu',
    menuClose: 'Close',
    anchors: {
      composition: 'Features',
      cases: 'Cases',
      templates: 'Templates',
      workflow: 'How we work',
      pricing: 'Pricing',
      contact: 'Contact',
    },
  },
  cta: {
    start: 'Get started',
    startBrief: 'Fill in the brief',
    calcEstimate: 'Estimate cost',
    moreInfo: 'Learn more',
    details: 'Details',
    showAll: 'Show all',
    showLess: 'Show less',
    reset: 'Reset',
    tryCalculator: 'Open calculator',
    requestDemo: 'Book a demo',
    downloadWhitepaper: 'Get the paper',
    getCaseBook: 'Get the case book',
    getPresentation: 'Get the deck',
    tryBuilder: 'Try it',
    bookSlot: 'Book a slot',
    bookVideoDemo: 'Book a video demo',
    sendPresentation: 'Email me the deck',
    subscribeNews: 'Subscribe',
    submitLead: 'Submit a request',
    backToHome: 'Back to home',
    writeUs: 'Write to us',
    sendForm: 'Send',
    sending: 'Sending…',
  },
  form: {
    submit: 'Send',
    submitting: 'Sending…',
    label: {
      name: 'Name',
      email: 'Email',
      phone: 'Phone',
      company: 'Company',
      message: 'Message',
      site: 'Company website',
      callTime: 'Best time to call',
    },
    placeholder: {
      email: "name{'@'}company.com",
      phone: '+44 20 7946 0958',
      company: 'Acme Inc.',
    },
    fields: {
      name: 'Name',
      email: 'Email',
      phone: 'Phone',
      company: 'Company',
      message: 'Message',
      messagePlaceholder: 'Briefly describe the task or timeline',
    },
    consent: {
      label: 'I accept the privacy policy',
    },
    httpsNote: 'Your data is protected and sent over HTTPS',
    notOffer: 'This is not a binding commercial offer.',
    optional: 'optional',
    validation: {
      required: 'This field is required.',
      email: "Enter a valid email, e.g. name{'@'}company.com",
      phone: 'Enter a valid phone number, e.g. +44 20 7946 0958',
      consent: 'Please accept the privacy policy before submitting.',
      minLength: 'Minimum {n} characters',
      maxLength: 'No more than {n} characters',
      submitFailed: 'We could not submit your request — try again or write to us directly',
    },
  },
  validation: {
    required: 'This field is required.',
    email: "Enter a valid email, e.g. name{'@'}company.com",
    phone: 'Enter a valid phone number, e.g. +44 20 7946 0958',
    consent: 'Please accept the privacy policy before submitting.',
    minLen: 'Minimum {n} characters',
    maxLen: 'No more than {n} characters',
    generic: 'We could not submit your request — try again or write to us directly',
  },
  toast: {
    success: {
      lead: 'Request sent — we will get back to you within a business day',
      caseBook: 'The case book has been sent to {email}. Check your inbox (and spam folder).',
    },
    error: {
      network: 'No connection. Check your internet and try again',
      generic: 'Something went wrong. Try again in a minute',
    },
    newsletter: {
      pending: 'Thanks! Your subscription will activate once we launch the newsletter.',
    },
    lead: {
      success: 'Request sent — we will get back to you within a business day',
      error: 'We could not submit your request — try again or write to us directly',
      pending: 'Sending…',
    },
  },
  modal: {
    thanksTitle: 'Thanks for trusting us!',
    thanksBody: 'We will get in touch shortly.',
    officeHours: 'Office hours — Mon–Fri, 10:00–19:00 Moscow time',
    exitIntentTitle: "Hold on! Don't leave empty-handed.",
    exitIntentBody: 'Leave a request and get our case book with links to the live sites.',
  },
  cookie: {
    banner: {
      text: 'We use cookies to keep the site running and make it easy to use. Details are in the privacy policy.',
    },
    accept: 'Accept',
    essential: 'Only necessary',
    configure: 'Configure',
    drawer: {
      title: 'Cookie settings',
      description:
        'Manage cookie categories. Strictly necessary cookies keep the site running and cannot be turned off.',
      save: 'Save settings',
    },
    categories: {
      essential: 'Strictly necessary',
      essentialDescription:
        'Needed for the site to work (session, routing, language). Cannot be disabled.',
      analytics: 'Analytics',
      analyticsDescription: 'Help us understand how people use the site. Data is anonymised.',
      marketing: 'Marketing',
      marketingDescription:
        'Used for relevant advertising and retargeting on third-party platforms.',
    },
  },
  notFound: {
    heading: 'Page not found',
    body: 'We may have renamed it, or you followed an outdated link.',
  },
  calculator: {
    heading: 'Cost calculator',
    next: 'Next',
    back: 'Back',
    submit: 'Get the results',
    saveAsPdf: 'Save as PDF',
    resume: 'Resume from saved step',
    startOver: 'Start over',
    resetConfirm: 'Reset all selected options and start over?',
    totalLabel: 'Preliminary total',
    currency: '₽',
    currencyNote: 'All prices in ₽ (RUB).',
    stepCounter: 'Step {current} of {total}',
    draftSaved: 'Draft saved',
    hint: 'The total is indicative. We lock it in after the brief.',
    resumedToast: 'Resuming from your saved step. You can start over from the menu.',
    submittedToast: 'Estimate sent to {email}. We will email the full PDF breakdown today.',
    pdfToast: 'PDF export will ship in the next release.',
    formTitle: 'How should we reach you?',
    formEmailLabel: 'Email',
    formPhoneLabel: 'Phone',
  },
  brief: {
    heading: 'Brief — "Let\'s get acquainted!"',
    next: 'Next',
    back: 'Back',
    skip: 'Skip',
    submit: 'Get the deck',
    resume: 'Resume filling',
    resetConfirm: 'Reset answers and start over?',
    stepCounter: 'Step {current} of {total}',
    draftSaved: 'Draft saved',
    resumedToast: 'Resuming from your saved step. You can start over from the menu.',
    submittedToast: 'Thanks! The deck will arrive at {email} within a business day.',
    customOptionLabel: 'Custom option (add your own text)',
    formTitle: 'How should we reach you?',
    formEmailLabel: 'Email',
  },
  aria: {
    skipToMain: 'Skip to main content',
    langSwitcher: 'Choose site language',
    scrollToTop: 'Back to top',
    closeModal: 'Close dialog',
    dismissToast: 'Dismiss notification',
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    openDropdown: 'Expand menu',
    loading: 'Loading',
    breadcrumbs: 'Breadcrumbs',
    promo: 'Promo message',
    cookieConsent: 'Cookie consent',
    cookieAnalytics: 'Toggle analytics cookies',
    cookieMarketing: 'Toggle marketing cookies',
    pricingTotal: 'Estimated total',
    workflowSteps: 'Project workflow steps',
    subproductSections: 'Product page sections',
  },
  breadcrumbs: {
    home: 'Home',
  },
  footer: {
    tagline: 'Launch promo campaigns of any complexity.',
    copyright: '© {year} OOO Bafsy (LLC Bafsy). All rights reserved.',
    legalEntity: 'OOO Bafsy',
    address: '41 Talalikhina St., Bldg. 9, Suite 1/4, Moscow',
    hours: 'Mon–Fri, 10:00–19:00 Moscow time',
    sections: {
      products: 'Products',
      company: 'Company',
      legal: 'Legal',
    },
    links: {
      blogSoon: 'Blog (soon)',
      contacts: 'Contacts',
      accelerator: 'Accelerator',
    },
    newsletter: {
      cta: 'Subscribe',
      placeholder: "name{'@'}company.com",
      heading: 'Subscribe to our newsletter',
      description: 'Ideas, cases and tools for standout promos — once a month.',
    },
  },
  promo: {
    ideasTelegram: 'Ideas, cases and tools for standout promos — subscribe',
    fmcgTelegram: 'The FMCG beat: industry news and behind-the-scenes facts',
    infoPartner: 'Media partner',
    dismiss: 'Dismiss promo',
  },
  status: {
    ready: 'Ready',
    beta: 'Beta',
    planned: 'Planned',
  },
  sections: {
    hero: {
      eyebrow: 'Promo campaign launch platform',
      heading: 'Cut promo launch cost and time by up to 4×',
      lead: 'Launch promo campaigns on Bafsy. Any mechanic, any scale — delivered end to end.',
      primaryCta: 'Start the brief',
      secondaryCta: 'Watch 3-min demo',
    },
    trust: {
      title: 'Numbers, certifications and a patent — on the record',
      subtitle:
        'We have launched 220+ campaigns with complex mechanics, cut launch time by 60%, and audit every project against a 63-point quality checklist.',
      logosLabel: 'Trusted by 249+ clients',
    },
    proof: {
      title: 'Trusted by',
      subtitle: 'More than 249 clients have launched promo campaigns on Bafsy.',
    },
    platform: {
      title: 'Bafsy stands on four pillars',
      subtitle:
        'The platform bundles 14 ready-made solutions, the PromoKit engine, a visual builder and out-of-the-box integrations.',
      showcase: {
        title: 'Under the hood: PromoKit inside Bafsy',
        subtitle:
          '16 live screens from CRM, builder and operations — the same surfaces we have used on 300+ FMCG promos.',
        intro:
          'The tour starts with picking a promo type and running a quick-launch checklist. From there it walks through the builders for signup forms, prizes, emails and receipts, pauses on gamification, and ends with a multi-channel launch — landing page, website and Telegram bot.',
        groupAll: 'All',
        groupBuilder: 'Builder',
        groupCrm: 'CRM',
        groupOperations: 'Operations',
        groupOther: 'Other',
        next: 'Next screen',
        prev: 'Previous screen',
        source: 'Source',
      },
    },
    solutions: {
      title: '14 ready-made solutions',
      subtitle: 'Plug in an existing solution or commission a new one tuned to your mechanic.',
      cardNotFound: {
        title: 'Do you want a custom special project?',
        subtitle: 'Leave a request and we will develop a unique offer for you',
      },
    },
    modules: {
      title: 'Turn on only the PromoKit modules you need',
      subtitle: 'No bundled bloat — pay only for the modules you actually use.',
    },
    visualBuilder: {
      title: 'Visual builder',
      subtitle: 'Assemble a promo site without pulling in developers.',
    },
    integrations: {
      title: 'Integrations and deployment',
      subtitle: 'Bafsy fits into your infrastructure — cloud or on-premise.',
      outcomesTitle: 'What you get',
    },
    workflow: {
      title: 'A full cycle in 6 stages',
      subtitle: 'From filling in the brief to running the live campaign.',
      receivesLabel: 'Client receives',
    },
    cases: {
      eyebrow: 'Cases',
      title: 'We have built 900+ promo sites',
      subtitle: "A few of the platform's recent launches.",
      carousel: {
        roledescription: 'Carousel',
        label: 'PromoKit cases',
        slideLabel: '{current} of {total}',
        prev: 'Previous case',
        next: 'Next case',
        goTo: 'Go to case {n}',
      },
    },
    templates: {
      eyebrow: 'Templates',
      title: 'Ready-to-launch promo templates',
      subtitle: 'Pick a format that fits your goal — launch in weeks, not months.',
      traitsLabel: "What's included",
      useCta: 'Use this template',
      timelineLabel: 'Timeline',
      modalTitle: 'Launch with the "{template}" template',
      modalLead: 'Tell us about your project — we will send a draft estimate and timeline.',
      messagePrefix: 'Template: {template}',
    },
    briefTeaser: {
      title: "Let's get acquainted!",
      subtitle: 'Answer a few questions and we will propose the right fit for your business.',
      bonus:
        'Complete the survey to receive our platform overview and a selection of case studies.',
    },
    pricingTeaser: {
      title: 'Keep project costs under control — easily',
      subtitle: 'Estimate the scope in the calculator and get a preliminary quote.',
      assurance: 'The price is locked — no surprise add-ons.',
    },
    blog: {
      title: 'Bafsy blog — coming soon',
      subtitle: 'Articles on launching promos and picking mechanics. Stay tuned.',
      soonLabel: 'Soon',
    },
    leadMagnet: {
      title: 'Get the case book',
      subtitle: 'Drop your email — we will send a PDF with links to live sites.',
      description: 'Drop your email — we will send a PDF with links to live sites.',
      fileLabel: 'PDF · 2.5 MB',
    },
    contact: {
      title: 'Request a project kickoff',
      subtitle: 'We will reserve a slot in production and reply during business hours.',
    },
    closing: {
      title: 'Ready to launch a promo?',
      subtitle: 'Start with the brief or price it in the calculator — 5 minutes either way.',
    },
  },
  pages: {
    home: {
      heroEyebrow: 'Bafsy promo platform',
    },
    brief: {
      heading: "Let's get acquainted!",
      lead: '4 short steps so we can pick the right tools and tailor a deck for your business.',
      heroEyebrow: 'Brief',
    },
    calculator: {
      heading: 'Keep project costs under control — easily',
      lead: 'Build a preliminary estimate in 7 steps and get a PDF with all the paperwork.',
      heroEyebrow: 'Cost calculator',
    },
    privacy: {
      heading: 'Privacy policy',
      lead: 'Legal documents, OOO Bafsy registration details and how we handle personal data.',
      content: {
        main: {
          title: 'Legal entity',
          companyName: 'OOO «Bafsy»',
          inn: 'INN',
          kpp: 'KPP',
          ogrn: 'OGRN',
          opd: 'OPD registration number',
          duns: 'DUNS',
        },
        address: {
          title: 'Address',
          subtitle: 'Legal and factual',
          address: '109316, Moscow, internal territory of Nizhegorodsky municipal district, Talalikhina street, 41, building 9, room 1/4.',
        },
        openingHours: {
          title: 'Opening hours',
          openingHours: 'Mon–Fri, 10:00–19:00 Moscow time.',
        },
        contacts: {
          title: 'Contacts',
        },
        achievements: {
          title: 'Certificates and Memberships',
          gost: 'GOST',
          labelCertificate: 'certificate of service quality',
          event1: {
            title: 'ARPP «Domestic Software»',
            labelCard: 'participant card',
            lableCertificate: 'certificate',
          },
          event2: {
            title: 'Union for the Support and Development of Technology Companies',
          },
          event3: {
            titleBegin: '',
            title: 'The Digital Partnership Award',
            titleEnd:
              '(October 2021) in the Digital Systems for Business category was selected from over 200 developers in Russia and the CIS.',
          },
        },
        legals: {
          title: 'Legal documents',
          lebalPersonalData: 'Personal data processing policy',
          lebalPolicy: 'Privacy Policy',
        },
        copyright: '«© OOO «Bafsy». All rights reserved.»',
      },
    },
    subproduct: {
      faqAnswerPlaceholder:
        'Happy to dive deeper on a brief or in direct messages — leave a request and we will get back within an hour.',
    },
    notFound: {
      heading: 'Page not found',
      lead: 'We may have renamed it, or you followed an outdated link.',
      body: 'Check the URL or head back to the home page — all the sections live there.',
      homeCta: 'Back to home',
      contactCta: 'Write to us',
    },
  },
  seo: {
    defaults: {
      title: 'Bafsy — promo platform',
      description:
        'Bafsy promo platform: 14 ready-made solutions, a zero-code builder and PromoKit modules.',
    },
    home: {
      title: 'A promo platform for standout campaigns',
      description:
        'Cut promo launch cost and time by up to 4×. 14 ready-made solutions, a zero-code builder and PromoKit modules.',
    },
  },
};

export default en;
